import { Proposal } from 'entities/Proposal/model/types/ProposalSchema';
import { Vendor, VendorActivationStatus } from 'entities/Vendors';

export enum ItemAvailabilityStatus {
    AVAILABLE = 'AVAILABLE',
    NO_SUPPLIERS = 'NO_SUPPLIERS',
    SUPPLIERS_NOT_ENABLED = 'SUPPLIERS_NOT_ENABLED',
}

/**
 * Returns the availability status of a catalog item:
 * - AVAILABLE: at least one supplier is VERIFIED
 * - NO_SUPPLIERS: no proposals exist for the item
 * - SUPPLIERS_NOT_ENABLED: proposals exist but no supplier is VERIFIED
 */
export const getItemAvailabilityStatus = (
    proposals: Proposal[] | undefined,
    vendorsMap: Record<string, Vendor> | null | undefined,
): ItemAvailabilityStatus => {
    if (!proposals || proposals.length === 0) {
        return ItemAvailabilityStatus.NO_SUPPLIERS;
    }

    if (!vendorsMap) {
        return ItemAvailabilityStatus.SUPPLIERS_NOT_ENABLED;
    }

    const hasVerifiedSupplier = proposals.some((proposal) => {
        const vendor = vendorsMap[proposal.seller.uid];
        return (
            vendor?.operationsAccountStatus === VendorActivationStatus.VERIFIED
        );
    });

    return hasVerifiedSupplier
        ? ItemAvailabilityStatus.AVAILABLE
        : ItemAvailabilityStatus.SUPPLIERS_NOT_ENABLED;
};

/**
 * Determines if an item is available to be added to property catalog.
 * An item is available if it has at least one proposal where the supplier
 * has product ordering enabled (VERIFIED status).
 *
 * NOTE: Renovations are not currently supported. When renovations feature
 * is added, this function will need to check both operationsAccountStatus
 * and renovationsAccountStatus (when renovationEnabled is true).
 */
export const isItemAvailable = (
    proposals: Proposal[] | undefined,
    vendorsMap: Record<string, Vendor> | null | undefined,
): boolean => {
    return (
        getItemAvailabilityStatus(proposals, vendorsMap) ===
        ItemAvailabilityStatus.AVAILABLE
    );
};

/**
 * Gets the activation status for a supplier from a proposal.
 *
 * NOTE: Renovations are not currently supported. When renovations feature
 * is added, this function will need to check renovationsAccountStatus
 * (when renovationEnabled is true) as an alternative path to VERIFIED status.
 */
export const getSupplierActivationStatus = (
    proposal: Proposal,
    vendorsMap: Record<string, Vendor> | null | undefined,
): VendorActivationStatus | null => {
    if (!vendorsMap) {
        return null;
    }

    const vendor = vendorsMap[proposal.seller.uid];
    if (!vendor) {
        return null;
    }

    return vendor.operationsAccountStatus;
};

/**
 * Determines if a supplier should show the "Enable product ordering" button.
 * Shows for suppliers that are not in vendor list OR not verified.
 * Does NOT show for Action Required (INVALID) status.
 *
 * NOTE: Renovations are not currently supported. When renovations feature
 * is added, this function will need to check both operationsAccountStatus
 * and renovationsAccountStatus (when renovationEnabled is true) to determine
 * if the supplier is verified.
 */
export const shouldShowEnableProductOrderingButton = (
    proposal: Proposal,
    vendorsMap: Record<string, Vendor> | null | undefined,
): boolean => {
    if (!vendorsMap) {
        // Vendors not loaded yet - don't show button to avoid misrouting to Add Vendor
        return false;
    }

    const vendor = vendorsMap[proposal.seller.uid];
    if (!vendor) {
        // Supplier not in vendor list - show button
        return true;
    }

    // Don't show for Action Required or Verifying status
    if (vendor.operationsAccountStatus === VendorActivationStatus.INVALID) {
        return false;
    }

    if (vendor.operationsAccountStatus === VendorActivationStatus.VERIFYING) {
        return false;
    }

    const isOperationsVerified =
        vendor.operationsAccountStatus === VendorActivationStatus.VERIFIED;

    // Show button if not verified
    return !isOperationsVerified;
};
